/**
 * CatalogContext — fetches services, banners, pricing-rules ONCE on mount
 * and caches them for the whole session. Pages that need only these three
 * datasets read from context instead of refetching.
 *
 * Per-page lists (packages, portfolio by service, etc.) are NOT cached here.
 */
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

import {
  listServices,
  listBanners,
  getPricingRules,
} from "./api";

const CatalogContext = createContext(null);

export function CatalogProvider({ children }) {
  const [services, setServices] = useState([]);
  const [banners, setBanners] = useState([]);
  const [pricingRules, setPricingRules] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [svc, bn, pr] = await Promise.all([
          listServices(),
          listBanners(),
          getPricingRules(),
        ]);
        if (cancelled) return;
        setServices(svc || []);
        setBanners(bn || []);
        setPricingRules(pr || null);
      } catch (e) {
        if (!cancelled) setError(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const value = useMemo(
    () => ({ services, banners, pricingRules, loading, error }),
    [services, banners, pricingRules, loading, error],
  );

  return (
    <CatalogContext.Provider value={value}>{children}</CatalogContext.Provider>
  );
}

export function useCatalog() {
  const ctx = useContext(CatalogContext);
  if (!ctx) throw new Error("useCatalog must be used inside CatalogProvider");
  return ctx;
}
